import React, { useState, useEffect } from 'react';
import { Save, Upload, MapPin, Phone, Mail, Globe } from 'lucide-react';
import api from '../../../api';
import './AccountSections.css';

const MyProfileSection = ({ userId }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    description: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    // First, check authentication status
    const checkAuth = async () => {
      try {
        const authCheck = await api.get('/brands/debug/auth');

      } catch (error) {
      }
    };

    checkAuth();
    fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfileData = async () => {
    try {
      setIsFetching(true);
      setMessage({ type: '', text: '' });

      // Get stored userId and role for debugging
      const storedUserId = localStorage.getItem('userId');
      const storedRole = localStorage.getItem('role');
      const token = localStorage.getItem('token');
      // Validate that user is logged in and has correct data
      if (!token) {
        throw new Error('No authentication token found. Please log in.');
      }

      if (!storedUserId) {
        throw new Error('User ID not found. Please log in again.');
      }

      // CRITICAL: Always use the stored userId, not the route param
      // This ensures we're accessing our own data, not someone else's
      const userIdToUse = storedUserId;

      const response = await api.get(`/brands/${userIdToUse}/profile`);

      if (response.data) {
        setFormData({
          companyName: response.data.companyName || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          website: response.data.website || '',
          address: response.data.address || '',
          city: response.data.city || '',
          state: response.data.state || '',
          zipCode: response.data.zipCode || '',
          country: response.data.country || '',
          description: response.data.description || ''
        });
      }
    } catch (error) {

      let errorMessage = 'Failed to load profile data';

      if (error.message && !error.response) {
        // Custom error thrown above
        errorMessage = error.message;
      } else if (error.response) {
        if (error.response.status === 404) {
          if (error.response.data && typeof error.response.data === 'object') {
            errorMessage = error.response.data.message || 'Brand profile not found. Please contact support.';
          } else {
            errorMessage = 'Brand profile not found. Please contact support.';
          }
        } else if (error.response.status === 403) {
          if (error.response.data && typeof error.response.data === 'object') {
            const responseData = error.response.data;
            if (responseData.message && responseData.message.includes('not a brand user')) {
              errorMessage = 'This account is not a brand account. Please log in with a brand account.';
            } else {
              errorMessage = responseData.message || 'Access denied. Please verify you are logged in with a brand account.';
            }
          } else {
            errorMessage = 'Access denied. Please verify you are logged in with a brand account.';
          }
        } else if (error.response.data) {
          if (typeof error.response.data === 'object' && error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          }
        }
      } else if (error.request) {
        errorMessage = 'Cannot connect to server. Please check your connection.';
      }

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post(`/brands/${userId}/profile`, formData);

      if (response.status === 200) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h2 className="section-title">My Profile</h2>
        <p className="section-description">Manage your brand profile information</p>
      </div>

      {isFetching && (
        <div className="loading-state">
          <p>Loading profile data...</p>
        </div>
      )}

      {!isFetching && (
        <>
          {message.text && (
            <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="companyName" className="form-label">
              Company Name *
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              <Mail size={16} className="inline mr-1" />
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              <Phone size={16} className="inline mr-1" />
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="website" className="form-label">
              <Globe size={16} className="inline mr-1" />
              Website
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="form-input"
              placeholder="www.example.com"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description" className="form-label">
            Company Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="form-textarea"
            rows="4"
            placeholder="Tell us about your brand..."
          />
        </div>

        <div className="form-section-title">
          <MapPin size={18} className="inline mr-2" />
          Address Information
        </div>

        <div className="form-group">
          <label htmlFor="address" className="form-label">
            Street Address
          </label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            className="form-input"
          />
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="city" className="form-label">
              City
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="state" className="form-label">
              State/Province
            </label>
            <input
              type="text"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="zipCode" className="form-label">
              ZIP/Postal Code
            </label>
            <input
              type="text"
              id="zipCode"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="country" className="form-label">
              Country
            </label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className="form-input"
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn-primary"
            disabled={isLoading}
          >
            <Save size={18} className="inline mr-2" />
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
        </>
      )}
    </div>
  );
};

export default MyProfileSection;

